import { useMutation, useQueryClient } from '@tanstack/react-query';

import { invalidateSupplierActivationQueries } from '../queryKeys';

import { postSupplierActivationPlan } from './postSupplierActivationPlan';
import { type PostSupplierActivationPlanMutationOptions } from './types';

/**
 * React Query based custom hook for creating a planned supplier activation.
 *
 * Invalidates the activation request, supplier detail, and supplier overview
 * queries — the customer number in the payload is shared with and persisted
 * on the supplier detail page.
 *
 * @param options - Optional mutation options.
 * @returns The mutation result.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Supplier-overview/operation/post_supplier_activation_plan_suppliers_actions__de_id__activation_plan_post
 */
export const useMutationPostSupplierActivationPlan = (
  options?: PostSupplierActivationPlanMutationOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ deId, payload }) =>
      postSupplierActivationPlan(deId, payload),
    onSettled(_data, _error, { deId }) {
      invalidateSupplierActivationQueries(queryClient, deId);
    },
    throwOnError: false,
    ...options,
  });
};
